/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

/**
 * useInstallPrompt – reads the PWA install prompt captured by the
 * inline <script> in index.html (window.__pwaInstallPrompt).
 *
 * The prompt is stored globally so it's captured before React loads.
 *
 * Returns:
 *   - canInstall:  true when the native install prompt is available
 *   - isInstalled: true if already running as installed PWA
 *   - install():   triggers the native install dialog
 */

import { useCallback, useSyncExternalStore } from 'react';

/* ── Subscribe to the global notifier set up in index.html ────── */

function _subscribe(cb) {
  window.__pwaListeners.add(cb);
  return () => window.__pwaListeners.delete(cb);
}

/* ── Hook ───────────────────────────────────────────────────────── */

export function useInstallPrompt() {
  const canInstall = useSyncExternalStore(
    _subscribe,
    () => window.__pwaInstallPrompt !== null && !window.__pwaInstalled
  );
  const isInstalled = useSyncExternalStore(
    _subscribe,
    () => window.__pwaInstalled
  );

  const install = useCallback(async () => {
    const prompt = window.__pwaInstallPrompt;
    if (!prompt) return false;

    prompt.prompt();
    const { outcome } = await prompt.userChoice;

    if (outcome === 'accepted') {
      window.__pwaInstallPrompt = null;
      window.__pwaNotify();
      return true;
    }
    return false;
  }, []);

  return { canInstall, isInstalled, install };
}

export default useInstallPrompt;
